"use client"

import { memo } from "react"
import useOrientation from "@/hooks/useOrientation"
import { motion } from "framer-motion"
import {
  ChevronDown,
  MoreVertical,
} from "lucide-react"

type Props = {
  title?: string
  subtitle?: string
  leftLabel?: string | number
  onClose?: () => void
  onMore?: () => void
  onHeaderClick?: () => void
}

function DrawerHeader({
  title,
  subtitle,
  leftLabel,
  onClose,
  onMore,
  onHeaderClick,
}: Props) {
  const { isStandalone } = useOrientation()

  return (
    <motion.div
      onPointerDown={onHeaderClick}
      className={`
        sticky top-0 z-30
        flex items-center justify-between
        px-4
        ${isStandalone ? "pt-20 pb-3" : "py-3"}
        select-none
      `}
      initial={{
        opacity: 0,
        y: -8,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      {/* LEFT */}
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onClose?.()
          }}
          className="
            flex h-9 w-9
            shrink-0
            items-center justify-center
            rounded-full
            border border-white/[0.06]
            bg-white/[0.025]
            text-white/65
            transition-all
            active:scale-95
            active:bg-white/[0.08]
            touch-manipulation
          "
          aria-label="Cerrar reproductor"
        >
          <ChevronDown size={19} />
        </button>

        <div
          className="flex min-w-0 flex-col"
          onClick={(event) => {
            event.stopPropagation()
            onHeaderClick?.()
          }}
        >
          <span
            className="
              truncate
              text-xs
              text-white/50
            "
          >
            {leftLabel ?? "Now Playing"}
          </span>

          <span
            className="
              truncate
              text-sm
              font-medium
              text-white
            "
          >
            {title ?? "Sin reproducción"}
          </span>
        </div>
      </div>

      {/* RIGHT */}
      <div
        className="flex shrink-0 items-center gap-2"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {subtitle && (
          <span
            className="
              hidden
              max-w-[140px]
              truncate
              text-xs
              text-white/40
              sm:block
            "
          >
            {subtitle}
          </span>
        )}

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onMore?.()
          }}
          className="
            flex h-9 w-9
            shrink-0
            items-center justify-center
            rounded-full
            border border-white/[0.06]
            bg-white/[0.025]
            text-white/65
            transition-all
            active:scale-95
            active:bg-white/[0.08]
            touch-manipulation
          "
          aria-label="Más opciones"
        >
          <MoreVertical size={19} />
        </button>
      </div>
    </motion.div>
  )
}

export default memo(DrawerHeader)